import { useMemo } from 'react';
import { Box, type BoxProps } from '@chakra-ui/react';

export interface CircularProgressProps extends Omit<BoxProps, 'children' | 'transitionDuration'> {
  // Progress value, 0-100
  value: number;
  // Progress bar width (stroke width)
  strokeWidth?: number | string;
  // Progress bar color
  strokeColor?: string;
  // Unfilled color
  trackColor?: string;
  // Size (width and height)
  size?: number | string;
}

/**
 *
 * @example
 * ```tsx
 * <CircularProgress
 *   value={75}
 *   strokeWidth={4}
 *   strokeColor="#1677ff"
 *   trackColor="#eeeeee"
 *   size="100px"
 *   animation="spin 1s linear infinite"
 * />
 * ```
 */
export function CircularProgress({
  value,
  strokeWidth = '6px',
  strokeColor = 'white',
  trackColor = 'transparent',
  size = '100%',
  ...boxProps
}: CircularProgressProps) {
  // Purpose: Ensure progress value is within valid range
  // Reason: Prevent rendering errors from invalid values
  const normalizedValue = Math.max(0, Math.min(100, value));

  // Purpose: Calculate SVG-related parameters
  // Reason: Avoid recalculating on every render to improve performance
  const svgConfig = useMemo(() => {
    const strokeWidthNum =
      typeof strokeWidth === 'string' ? parseFloat(strokeWidth) || 2 : strokeWidth;

    // SVG viewBox size
    const viewBoxSize = 100;
    // Circle radius (considering stroke width to avoid overflow)
    const radius = (viewBoxSize - strokeWidthNum) / 2;
    // Circle circumference
    const circumference = 2 * Math.PI * radius;
    // Offset corresponding to filled arc length (starting from top, clockwise)
    const offset = circumference - (normalizedValue / 100) * circumference;

    return {
      viewBoxSize,
      radius,
      circumference,
      offset,
      strokeWidthNum,
    };
  }, [strokeWidth, normalizedValue]);

  return (
    <Box
      display="inline-block"
      position="relative"
      width={size}
      height={size}
      aspectRatio="1"
      {...boxProps}
    >
      <svg
        width="100%"
        height="100%"
        viewBox={`0 0 ${svgConfig.viewBoxSize} ${svgConfig.viewBoxSize}`}
        style={{
          // Purpose: Rotate SVG so progress starts from top
          // Reason: SVG circle starts from right by default, rotate -90deg to start from top
          transform: 'rotate(-90deg)',
          transformOrigin: 'center',
        }}
      >
        {/* Purpose: Background circle ring, showing unfilled portion */}
        {/* Reason: Provide visual contrast so users can see total progress */}
        <circle
          cx={svgConfig.viewBoxSize / 2}
          cy={svgConfig.viewBoxSize / 2}
          r={svgConfig.radius}
          fill="none"
          stroke={trackColor}
          strokeWidth={svgConfig.strokeWidthNum}
          strokeLinecap="round"
        />
        {/* Purpose: Progress circle ring, showing filled portion */}
        {/* Reason: Control progress display through stroke-dasharray and stroke-dashoffset */}
        <circle
          cx={svgConfig.viewBoxSize / 2}
          cy={svgConfig.viewBoxSize / 2}
          r={svgConfig.radius}
          fill="none"
          stroke={strokeColor}
          strokeWidth={svgConfig.strokeWidthNum}
          strokeLinecap="round"
          strokeDasharray={svgConfig.circumference}
          strokeDashoffset={svgConfig.offset}
          style={{
            // Purpose: Add smooth transition for progress changes (internal handling)
            // Reason: Make progress updates smooth and visually appealing, always enabled
            transition: `stroke-dashoffset 300ms ease-in-out`,
          }}
        />
      </svg>
    </Box>
  );
}
